import type { LdsVideoPlayer } from "@lds/core/lds-video-player.d.ts"
import type { default as VideoJsPlayer } from "video.js/dist/types/player"
import { gallerySlider } from "./gallery-elements"
import "@lds/core/lds-video-player.js"

/**
 * Swiper builds its endless loop by relocating real slide elements inside the wrapper
 * (`loopFix()` calls `prepend()`/`append()`). Relocating disconnects the slide for a
 * moment, and `LdsVideoPlayer.disconnectedCallback()` reacts to that by disposing its
 * video.js player: the <video-js> element is removed from the shadow root while
 * `inited` stays `true`, which leaves the slide as a black box. Touching such a player
 * throws, which used to break slide navigation and closing of the gallery as well.
 *
 * A disposed player cannot be revived, so every player is remembered in its pristine
 * state and the broken element is swapped for a fresh copy after a loop fix.
 */
const pristineVideoPlayers: WeakMap<LdsVideoPlayer, LdsVideoPlayer> = new WeakMap()

const videoPlayers = (): Array<LdsVideoPlayer> => [
    ...((gallerySlider()?.querySelectorAll("lds-video-player") ?? []) as NodeListOf<LdsVideoPlayer>),
]

function hasDisposedPlayer(videoPlayer: LdsVideoPlayer): boolean {
    const player: VideoJsPlayer | undefined = videoPlayer.player

    return player?.isDisposed() === true
}

function togglePlusOverlays(videoPlayer: LdsVideoPlayer, hidden: boolean): void {
    const overlays: NodeListOf<HTMLElement> | undefined = videoPlayer
        .closest(".video-slide")
        ?.querySelectorAll(".ladies-plus-thumb-overlay .plus-logo, .ladies-plus-thumb-overlay .video-play-plus")

    for (const overlay of overlays ?? []) {
        overlay.classList.toggle("hidden", hidden)
    }
}

// ToDo: Instead of this convoluted way of initializing videos, we should use metadata in the generator to add data-width
function updateVideoPlayerSize(videoPoster: HTMLImageElement | null, videoPlayer: LdsVideoPlayer): void {
    const videoSlide = videoPlayer.closest(".video-slide") as HTMLElement | null
    if (!videoPoster || !videoSlide) {
        return
    }
    setTimeout(() => {
        const width = videoPoster.naturalWidth
        const height = videoPoster.naturalHeight

        videoSlide.style.setProperty("--video-width", `${width}`)
        videoSlide.style.setProperty("--video-height", `${height}`)
    }, 0)
}

//  and data-height attributes to the video player element, once it is implemented in the events
export function initializeVideos(): void {
    for (const videoPlayer of videoPlayers()) {
        prepareVideoPlayer(videoPlayer)
    }
}

function prepareVideoPlayer(videoPlayer: LdsVideoPlayer): void {
    pristineVideoPlayers.set(videoPlayer, videoPlayer.cloneNode(true) as LdsVideoPlayer)

    videoPlayer.addEventListener("ready", (): void => {
        if (!videoPlayer.player.videoWidth()) {
            videoPlayer.player.on("loadedmetadata", () => updatePlayerDimensions(videoPlayer).then())
        } else {
            updatePlayerDimensions(videoPlayer).then()
        }
    })

    const videoPoster = videoPlayer.querySelector(".video-poster") as HTMLImageElement | null

    if (!videoPoster?.complete) {
        videoPoster?.addEventListener("load", (): void => {
            updateVideoPlayerSize(videoPoster, videoPlayer)
        })
    } else {
        updateVideoPlayerSize(videoPoster, videoPlayer)
    }

    videoPlayer.renderRoot.addEventListener("ready", (): void => {
        videoPlayer.root.querySelector(".vjs-control-bar")?.classList.add("swiper-no-swiping")
    })

    videoPoster?.addEventListener("click", (): void => {
        videoPoster.classList.add("hidden")
        togglePlusOverlays(videoPlayer, true)
        videoPlayer.init()
    })
}

/**
 * Replaces every video player whose video.js instance was disposed by a slide
 * relocation, so that the slide shows its poster again and can be played once more.
 */
export function restoreDisposedVideoPlayers(): void {
    for (const videoPlayer of videoPlayers()) {
        if (hasDisposedPlayer(videoPlayer)) {
            replaceWithPristineVideoPlayer(videoPlayer)
        }
    }
}

function replaceWithPristineVideoPlayer(disposedVideoPlayer: LdsVideoPlayer): void {
    const pristineVideoPlayer: LdsVideoPlayer | undefined = pristineVideoPlayers.get(disposedVideoPlayer)
    if (!pristineVideoPlayer) {
        return
    }

    // The player is already gone - keep `disconnectedCallback()` from disposing it a second time.
    disposedVideoPlayer.player = undefined as unknown as VideoJsPlayer

    const replacement: LdsVideoPlayer = pristineVideoPlayer.cloneNode(true) as LdsVideoPlayer
    disposedVideoPlayer.replaceWith(replacement)

    prepareVideoPlayer(replacement)
    togglePlusOverlays(replacement, false)
}

export function pauseVideos(): void {
    for (const videoPlayer of videoPlayers()) {
        if (videoPlayer.inited && !hasDisposedPlayer(videoPlayer)) {
            videoPlayer.player.pause()
        }
    }
}

async function updatePlayerDimensions(videoPlayer: LdsVideoPlayer): Promise<void> {
    const player: VideoJsPlayer = videoPlayer.player
    player.pause()

    const videoWidth: number = player.videoWidth()
    const videoHeight: number = player.videoHeight()

    const videoSlide: HTMLElement | null = videoPlayer.closest(".video-slide") as HTMLElement | null
    if (!videoSlide) {
        return
    }

    player.on("play", (): void => {
        togglePlusOverlays(videoPlayer, true)
    })
    player.on("pause", (): void => {
        togglePlusOverlays(videoPlayer, false)
    })

    videoSlide.style.setProperty("--video-width", `${videoWidth}`)
    videoSlide.style.setProperty("--video-height", `${videoHeight}`)
}
